import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Select, MenuItem, FormControl,
    Chip, Paper, Divider, CircularProgress, Alert,
    Autocomplete, Stack
} from '@mui/material';
import * as api from './brandsApi';
import type {
    Brand, Company, BrandCategory, BarcodeType, SalesChannel,
    MainGroup, Location, TenantConfig
} from './brandsApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface BrandViewProps {
    brandId: number | null;
}

export function BrandView({ brandId }: BrandViewProps) {
    const { t } = useTranslation();

    const [brand, setBrand] = useState<Brand | null>(null);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [brandCategories, setBrandCategories] = useState<BrandCategory[]>([]);
    const [barcodeTypes, setBarcodeTypes] = useState<BarcodeType[]>([]);
    const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([]);
    const [allMainGroups, setAllMainGroups] = useState<MainGroup[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
    const [brandConnectEnabled, setBrandConnectEnabled] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [isNewBrand, setIsNewBrand] = useState(false);
    const [brandCopy, setBrandCopy] = useState<Brand | null>(null);
    const [selectedMainGroups, setSelectedMainGroups] = useState<MainGroup[]>([]);
    const [brandConnectSelectedLocations, setBrandConnectSelectedLocations] = useState<Location[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isNew = brandId == null;

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [companiesData, categoriesData, typesData, channelsData, mainGroupsData, locationsData, configData, bcEnabled] = await Promise.all([
                api.getCompanies(),
                api.getBrandCategories(),
                api.getBarcodeTypes(),
                api.getSalesChannels(),
                api.getMainGroups(),
                api.getLocations(),
                api.getTenantConfig(),
                api.isBrandConnectEnabled(),
            ]);

            setCompanies(companiesData);
            setBrandCategories(categoriesData);
            setBarcodeTypes(typesData);
            setSalesChannels(channelsData);
            setAllMainGroups(mainGroupsData);
            setLocations(locationsData);
            setTenantConfig(configData);
            setBrandConnectEnabled(bcEnabled);

            if (isNew) {
                const newBrand: Brand = {
                    id: 0,
                    brandCode: '',
                    brandDescription: '',
                    isActive: true,
                    useAlternativeArticleNumber: false,
                    useSupplierArticleNumber: false,
                    isGroupSaleBrand: false,
                    company: companiesData[0] ?? null,
                    companies: [],
                    brandCategory: null,
                    barcodeType: null,
                    salesChannels: [],
                    itemGroups: [],
                    brandConnect: null,
                };
                setBrand(newBrand);
                setIsNewBrand(true);
                setIsEditing(true);
            } else {
                const brandData = await api.getBrand(brandId as number);
                if (!brandData) throw new Error(`Brand ${brandId} returned empty response`);
                brandData.companies = brandData.companies ?? [];
                brandData.itemGroups = brandData.itemGroups ?? [];
                brandData.salesChannels = brandData.salesChannels ?? [];
                setBrand(brandData);
                setIsNewBrand(false);
                setIsEditing(false);
            }
        } catch (err) {
            console.error('BrandView load failed:', err);
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [brandId, isNew]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    if (loading) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!brand) return null;

    // Validation
    const isBrandCodeValid = (): boolean => {
        if (!tenantConfig || !brand.brandCode) return false;
        const isAssigned = tenantConfig.assignedBrandCodes.includes(brand.brandCode);
        const isNumeric = !isNaN(parseFloat(brand.brandCode)) && isFinite(Number(brand.brandCode));
        return !isAssigned && isNumeric && brand.brandCode.length === tenantConfig.articleBrandCodeLength;
    };

    const isBrandCategoryValid = (): boolean => !!brand.brandCategory && brand.brandCategory.id > 0;
    const isBarcodeTypeValid = (): boolean => !!brand.barcodeType && brand.barcodeType.id > 0;
    const isBrandDescriptionValid = (): boolean => !!brand.brandDescription && brand.brandDescription.length > 0;

    const isAllValid = (): boolean => {
        if (!isNewBrand) return isBrandDescriptionValid();
        return isBrandCodeValid() && isBrandCategoryValid() && isBarcodeTypeValid();
    };

    // Available main groups (not already on brand)
    const availableMainGroups = allMainGroups
        .filter(mg => !brand.itemGroups.some(ig => ig.mainGroup.id === mg.id))
        .sort((a, b) => a.mainGroupCode.localeCompare(b.mainGroupCode));

    // Actions
    const enableEditing = () => {
        setBrandCopy(JSON.parse(JSON.stringify(brand)));
        setIsEditing(true);
    };

    const cancelEditing = () => {
        if (brandCopy) setBrand(brandCopy);
        setIsEditing(false);
        setSelectedMainGroups([]);
    };

    const saveBrand = async () => {
        setSaving(true);
        setError(null);
        try {
            // Ensure preferred company is in companies list
            if (brand.company && !brand.companies.some(c => c.id === brand.company!.id)) {
                brand.companies.push(brand.company);
            }

            if (isNewBrand) {
                const response = await api.createBrand(brand, selectedMainGroups, [], []);
                setBrand(response);
                setIsNewBrand(false);
                setIsEditing(false);
                setSelectedMainGroups([]);
            } else {
                const response = await api.updateBrand(brand, selectedMainGroups, [], []);
                setBrand(response);
                setIsEditing(false);
                setSelectedMainGroups([]);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const addMainGroups = async () => {
        if (selectedMainGroups.length === 0) return;
        setSaving(true);
        try {
            await api.createItemGroups(selectedMainGroups, [brand]);
            const refreshed = await api.getBrand(brand.id);
            setBrand(refreshed);
            setSelectedMainGroups([]);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const connectSupplier = async () => {
        if (brandConnectSelectedLocations.length === 0) return;
        setSaving(true);
        try {
            const locationIds = brandConnectSelectedLocations.map(l => l.id);
            await api.connectBrandSupplier(brand.id, brand.brandConnect!.supplierName, locationIds);
            const refreshed = await api.getBrand(brand.id);
            setBrand(refreshed);
            setBrandConnectSelectedLocations([]);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const disconnectSupplier = async () => {
        setSaving(true);
        try {
            await api.disconnectBrandSupplier(brand.id);
            const refreshed = await api.getBrand(brand.id);
            setBrand(refreshed);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const updateBrandField = <K extends keyof Brand>(field: K, value: Brand[K]) => {
        setBrand({ ...brand, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${brand.brandCode} ${brand.brandDescription}`}
                isEditing={isEditing}
                isNew={isNewBrand}
                saving={saving}
                canSave={isAllValid()}
                onEdit={enableEditing}
                onSave={saveBrand}
                onCancel={cancelEditing}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    {/* Brand Code */}
                    <FormRow label={t('brand.brandcode')}>
                        {!isNewBrand ? (
                            <Typography sx={sx14}>{brand.brandCode}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={brand.brandCode}
                                onChange={e => updateBrandField('brandCode', e.target.value)}
                                error={isEditing && !isBrandCodeValid()}
                            />
                        )}
                    </FormRow>

                    {/* Brand Description */}
                    <FormRow label={t('brand.branddescription')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{brand.brandDescription}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={brand.brandDescription}
                                onChange={e => updateBrandField('brandDescription', e.target.value)}
                                error={!isBrandDescriptionValid()}
                            />
                        )}
                    </FormRow>

                    {/* Preferred Company */}
                    <FormRow label={t('brand.preferredCompany')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{brand.company?.companyName}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={brand.company?.id ?? ''}
                                    onChange={e => {
                                        const comp = companies.find(c => c.id === e.target.value);
                                        if (comp) updateBrandField('company', comp);
                                    }}
                                >
                                    {companies.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Companies */}
                    <FormRow label={t('brand.companies')}>
                        {!isEditing ? (
                            <Box>
                                {brand.companies
                                    .sort((a, b) => a.companyName.localeCompare(b.companyName))
                                    .map(c => <Typography sx={sx14} key={c.id}>{c.companyName}</Typography>)}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={companies}
                                getOptionLabel={o => o.companyName}
                                value={brand.companies}
                                onChange={(_, value) => updateBrandField('companies', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} placeholder={t('article.addCompany')} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((c, i) => <Chip {...getTagProps({ index: i })} key={c.id} label={c.companyName} size="small" />)
                                }
                            />
                        )}
                    </FormRow>

                    {/* Brand Category */}
                    <FormRow label={t('brand.brandcategory')}>
                        {!isNewBrand ? (
                            <Typography sx={sx14}>
                                {brand.brandCategory?.typeName}
                                {brand.brandCategory && (
                                    brand.brandCategory.isBarcodeGenerationAllowed
                                        ? ` (${t('brand.barcodegenerationallowed')})`
                                        : ` (${t('brand.barcodegenerationdisallowed')})`
                                )}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!isBrandCategoryValid()}>
                                <Select
                                    value={brand.brandCategory?.id ?? ''}
                                    onChange={e => {
                                        const cat = brandCategories.find(c => c.id === e.target.value);
                                        if (cat) updateBrandField('brandCategory', cat);
                                    }}
                                >
                                    {brandCategories.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.typeName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Barcode Type */}
                    <FormRow label={t('article.barcodetype')}>
                        {!isNewBrand ? (
                            <Typography sx={sx14}>{brand.barcodeType?.barcodeTypeName}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!isBarcodeTypeValid()}>
                                <Select
                                    value={brand.barcodeType?.id ?? ''}
                                    onChange={e => {
                                        const bt = barcodeTypes.find(b => b.id === e.target.value);
                                        if (bt) updateBrandField('barcodeType', bt);
                                    }}
                                >
                                    {barcodeTypes.map(b => (
                                        <MenuItem key={b.id} value={b.id}>{b.barcodeTypeName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Sales Channels */}
                    <FormRow label={t('brand.salesChannels')}>
                        {!isEditing ? (
                            <Box>
                                {brand.salesChannels.map(sc => (
                                    <Typography sx={sx14} key={sc.id}>{sc.salesChannelCode} {sc.salesChannelDescription}</Typography>
                                ))}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={salesChannels}
                                getOptionLabel={o => `${o.salesChannelCode} ${o.salesChannelDescription}`}
                                value={brand.salesChannels}
                                onChange={(_, value) => updateBrandField('salesChannels', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((sc, i) => (
                                        <Chip {...getTagProps({ index: i })} key={sc.id} label={`${sc.salesChannelCode} ${sc.salesChannelDescription}`} size="small" />
                                    ))
                                }
                            />
                        )}
                    </FormRow>

                    {/* Brand Connect */}
                    {!isNewBrand && brandConnectEnabled && brand.brandConnect?.status && (
                        <>
                            <Divider />
                            <Typography sx={sx14} fontWeight="bold">{t('brand.brandConnectSupplier')}</Typography>

                            {brand.brandConnect.status === 'Connected' && (
                                <Stack spacing={1}>
                                    <FormRow label={t('brand.brandConnectSupplier')}>
                                        <Typography sx={sx14}>{brand.brandConnect.supplierName}</Typography>
                                    </FormRow>
                                    <FormRow label={t('brand.brandConnectLocations')}>
                                        <Box>
                                            {brand.brandConnect.connectedLocations?.map(loc => (
                                                <Typography sx={sx14} key={loc}>{loc}</Typography>
                                            ))}
                                        </Box>
                                    </FormRow>
                                    <Box>
                                        <Button variant="contained" color="error" size="small" onClick={disconnectSupplier} disabled={saving} sx={{ color: '#fff' }}>
                                            {t('brand.brandConnectDisconnect')}
                                        </Button>
                                    </Box>
                                </Stack>
                            )}

                            {brand.brandConnect.status === 'Pending' && (
                                <Stack spacing={1}>
                                    <FormRow label={t('brand.brandConnectSupplier')}>
                                        <Typography sx={sx14}>{brand.brandConnect.supplierName}</Typography>
                                    </FormRow>
                                    <FormRow label={t('brand.brandConnectLocations')}>
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={locations}
                                            getOptionLabel={o => `${o.locationNumber} ${o.locationName}`}
                                            value={brandConnectSelectedLocations}
                                            onChange={(_, value) => setBrandConnectSelectedLocations(value)}
                                            isOptionEqualToValue={(o, v) => o.id === v.id}
                                            renderInput={params => <TextField {...params} placeholder={t('brand.brandConnectSelectLocations')} />}
                                        />
                                    </FormRow>
                                    <Box>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="small"
                                            onClick={connectSupplier}
                                            disabled={brandConnectSelectedLocations.length === 0 || saving}
                                        >
                                            {t('brand.brandConnectAccept')}
                                        </Button>
                                    </Box>
                                </Stack>
                            )}
                        </>
                    )}

                    {/* Main Groups */}
                    {!isNewBrand && (
                        <>
                            <Divider />
                            <FormRow label={`${t('brand.maingroups')}:`}>
                                <Stack spacing={1}>
                                    {isEditing && (
                                        <Box display="flex" gap={1}>
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                fullWidth
                                                options={availableMainGroups}
                                                getOptionLabel={o => `${o.mainGroupCode} ${o.mainGroupDescription}`}
                                                value={selectedMainGroups}
                                                onChange={(_, value) => setSelectedMainGroups(value)}
                                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                                renderInput={params => <TextField {...params} placeholder={t('article.addMaingroup')} />}
                                            />
                                            {selectedMainGroups.length > 0 && (
                                                <Button variant="outlined" color="inherit" size="small" onClick={addMainGroups} disabled={saving}>
                                                    +
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                    {brand.itemGroups
                                        .sort((a, b) => a.mainGroup.mainGroupDescription.localeCompare(b.mainGroup.mainGroupDescription))
                                        .map(ig => (
                                            <Typography sx={sx14} key={ig.mainGroup.id}>
                                                {ig.mainGroup.mainGroupCode} {ig.mainGroup.mainGroupDescription}
                                            </Typography>
                                        ))}
                                </Stack>
                            </FormRow>
                        </>
                    )}
                </Stack>
            </Box>

            {saving && (
                <Box display="flex" justifyContent="center" p={1}>
                    <CircularProgress size={20} />
                </Box>
            )}
        </Paper>
    );
}

